/** @jsx jsx */
import { jsx } from '@emotion/core';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import Spacer from '../Spacer';
import { Container } from '../../../styles/Grid';
import { hideElementCss } from '../../../utils/responsive';
import { MediaQueries } from '../../../styles/Grid';
import { Icon } from '../../ui/Icon';
import { paginate } from '../../../utils/paginate';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import {
  activePage,
  arrowBtnCss,
  arrowItemCss,
  labelContainerCss,
  leftArrowBtnCss,
  leftArrowItemCss,
  pageNumberCss,
  pagesContainerCss,
  paginationContainerCss,
  paginationItemCss,
  paginationLabelCss,
  rightArrowItemCss,
  rightArrowBtnCss,
} from './index.styles';

class Pagination extends SafePureComponent {
  safeRender() {
    const {
      baseUrl,
      currentPage,
      itemsPerPage,
      maxPages,
      pageParameterName,
      ofLabel,
      resultsLabel,
      position,
      searchQuery,
      searchQueryParameterName,
      totalSearchResults,
      location,
    } = this.props;

    const paginationSettings = paginate(
      totalSearchResults,
      currentPage,
      itemsPerPage,
      maxPages,
    );

    const paginationContainerAlignItems = {
      center: {
        alignItems: 'center',
      },
      left: {
        alignItems: 'flex-start',
      },
      right: {
        alignItems: 'flex-end',
      },
    };

    const getMarginRight = () => ({
      marginRight: 0,
    });

    const buildUrl = pageNumber => {
      let queryParams = queryString.parse(location.search);
      queryParams[pageParameterName] = pageNumber;

      if (searchQuery) {
        queryParams[searchQueryParameterName] = searchQuery;
      }

      const stringifiedQueryParams = queryString.stringify(queryParams);

      return `${baseUrl}?${stringifiedQueryParams}`;
    };

    const createPages = () =>
      paginationSettings.pages.map((pageNumber, index) => (
        <li
          css={[
            paginationItemCss,
            currentPage === pageNumber && activePage,
            pageNumber === paginationSettings.endPage && getMarginRight(),
          ]}
          key={index}
        >
          <a css={pageNumberCss} href={buildUrl(pageNumber)}>
            {pageNumber}
          </a>
        </li>
      ));

    const renderLeftArrow = () => (
      <a
        css={[arrowBtnCss, leftArrowBtnCss]}
        name="Previous image"
        aria-label="Previous image"
        href={buildUrl(currentPage - 1)}
      >
        <Icon name="arrow-left" />
      </a>
    );

    const renderRightArrow = () => (
      <a
        css={[arrowBtnCss, rightArrowBtnCss]}
        name="Next image"
        aria-label="Next image"
        href={buildUrl(currentPage + 1)}
      >
        <Icon name="arrow-right" />
      </a>
    );

    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === paginationSettings.totalPages;

    return (
      Boolean(totalSearchResults) && (
        <Spacer>
          <Container
            css={[
              paginationContainerCss,
              paginationContainerAlignItems[position],
            ]}
          >
            <ul css={[pagesContainerCss, hideElementCss(MediaQueries.sm)]}>
              {!isFirstPage && (
                <li css={[paginationItemCss, arrowItemCss, leftArrowItemCss]}>
                  {renderLeftArrow()}
                </li>
              )}
              {createPages()}
              {!isLastPage && (
                <li css={[paginationItemCss, arrowItemCss, rightArrowItemCss]}>
                  {renderRightArrow()}
                </li>
              )}
            </ul>
            <div css={labelContainerCss}>
              {!isFirstPage && (
                <div css={hideElementCss(MediaQueries.mdAndUp)}>
                  {renderLeftArrow()}
                </div>
              )}
              <p css={paginationLabelCss}>
                {`${paginationSettings.firstPageItem}-${paginationSettings.lastPageItem} ${ofLabel} ${totalSearchResults} ${resultsLabel}`}
              </p>
              {!isLastPage && (
                <div css={hideElementCss(MediaQueries.mdAndUp)}>
                  {renderRightArrow()}
                </div>
              )}
            </div>
          </Container>
        </Spacer>
      )
    );
  }
}

Pagination.displayName = 'Pagination';

Pagination.defaultProps = {
  baseUrl: '',
  currentPage: 1,
  itemsPerPage: 12,
  maxPages: 5,
  pageParameterName: 'page',
  ofLabel: 'of',
  resultsLabel: 'results',
  position: 'center',
  searchQueryParameterName: 'q',
  searchQuery: '',
  totalSearchResults: 0,
};

Pagination.propTypes = {
  baseUrl: PropTypes.string,
  currentPage: PropTypes.number,
  itemsPerPage: PropTypes.number,
  maxPages: PropTypes.number,
  pageParameterName: PropTypes.string,
  ofLabel: PropTypes.string,
  resultsLabel: PropTypes.string,
  position: PropTypes.oneOf(['center', 'left', 'right']),
  searchQuery: PropTypes.string,
  searchQueryParameterName: PropTypes.string,
};

export default withRouter(Pagination);
